/**
 * Marks a row in `AddInIds.userUiSettings` as user interface settings.
 *
 * That bucket holds nothing else, so the marker is not needed to tell rows apart — it is kept
 * so a row found in the wrong place, or written by an older version, is still recognisable.
 */
export const USER_UI_SETTINGS_DATA_TYPE = "userUiSettings";

export type UserUiSettings = Record<string, unknown>;

export interface UserUiSettingsAddInData<TSettings extends UserUiSettings = UserUiSettings> {
  id: string;
  dataType: typeof USER_UI_SETTINGS_DATA_TYPE;
  /** Which part of the interface these settings belong to, e.g. `faultMonitor.dateRange`. */
  scope: string;
  settings: Partial<TSettings>;
}

/**
 * Record id for one user's settings in one scope.
 *
 * All users' rows share the add-in bucket, so the user id has to be part of the id — the same
 * convention `presetUserId` and `viewOverrideId` already use.
 */
export function userUiSettingsRecordId(userId: string, scope: string) {
  return `${USER_UI_SETTINGS_DATA_TYPE}::${userId}::${scope}`;
}

export function createUserUiSettingsRecord<TSettings extends UserUiSettings>(
  userId: string,
  scope: string,
  settings: Partial<TSettings>,
): UserUiSettingsAddInData<TSettings> {
  return {
    id: userUiSettingsRecordId(userId, scope),
    dataType: USER_UI_SETTINGS_DATA_TYPE,
    scope,
    settings,
  };
}

export function isUserUiSettingsRecord<TSettings extends UserUiSettings>(
  details: unknown,
  userId: string,
  scope: string,
): details is UserUiSettingsAddInData<TSettings> {
  if (details === null || typeof details !== "object") {
    return false;
  }
  const candidate = details as Partial<UserUiSettingsAddInData>;
  return (
    candidate.dataType === USER_UI_SETTINGS_DATA_TYPE &&
    candidate.scope === scope &&
    candidate.id === userUiSettingsRecordId(userId, scope)
  );
}

export interface PickSettingsInput<TSettings extends UserUiSettings> {
  /** The shape and the fallback values. Only keys present here are ever returned. */
  defaults: TSettings;
  /** What this browser last saw, available synchronously. */
  mirror?: Partial<TSettings> | null;
  /** What is stored for this user, available once the add-in data has loaded. */
  remote?: Partial<TSettings> | null;
  /** Whether the stored settings are known yet. */
  remoteLoaded: boolean;
}

/**
 * Chooses which stored settings to show.
 *
 * Before the stored record has arrived the browser's own copy is used, so a page does not flash
 * its defaults on every load. Once the record is known it wins: the browser copy can be weeks
 * old on a second device, and preferring it there would quietly undo a change made elsewhere.
 * A user with no record yet keeps their browser copy, so a first visit is not thrown away by an
 * empty sync.
 *
 * Only keys present in `defaults` are returned, and only with the type the default has — a
 * setting that was renamed, removed or stored with the wrong type cannot leak back in.
 */
export function pickSettings<TSettings extends UserUiSettings>(
  input: PickSettingsInput<TSettings>,
): TSettings {
  const { defaults, mirror, remote, remoteLoaded } = input;
  const stored = remoteLoaded && remote ? remote : mirror;
  const result = { ...defaults };

  if (!stored) {
    return result;
  }

  for (const key of Object.keys(defaults) as (keyof TSettings)[]) {
    const value = stored[key];
    if (value !== undefined && typeof value === typeof defaults[key]) {
      result[key] = value as TSettings[keyof TSettings];
    }
  }

  return result;
}
